import { mutate } from "swr";
import { apiClient, apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;
const MEMORIES_API_PREFIX = `${VITE_CONVERSATIONS_API}/memories`;

/**
 * A memory as it appears in the registry list: when it is relevant, but not
 * what it says. The service omits `content` here so assembling the registry
 * stays cheap; read it with `getMemory`.
 */
export interface PartialMemory {
  id: string;
  name: string;
  description: string;
  reference_conversation_id: string | null;
  reference_turn_id: string | null;
  total_invocations: number;
  last_accessed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Memory extends PartialMemory {
  content: string;
}

/**
 * Where a memory was written, resolved against the database. The service only
 * answers when the conversation and the turn are both still there, so having one
 * of these is what makes a link safe to render.
 */
export interface MemorySource {
  conversation_id: string;
  // The project as it appears in a conversation URL.
  project_id: string;
  turn_id: string;
}

export const memoriesListPath = () => `${MEMORIES_API_PREFIX}/`;

export const memoryPath = (memoryId: string) => `${MEMORIES_API_PREFIX}/${memoryId}`;

export const memorySourcePath = (memoryId: string) => `${memoryPath(memoryId)}/source`;

export function mutateMemoryList(): Promise<unknown> {
  return mutate(memoriesListPath());
}

/**
 * Read one memory in full. This is the uncounted read — it leaves
 * `total_invocations` alone, unlike the agent's `POST /memories/load`.
 */
export async function getMemory(memoryId: string): Promise<Memory> {
  return apiClient<Memory>(memoryPath(memoryId));
}

export async function deleteMemory(memoryId: string): Promise<void> {
  await apiMutate(memoryPath(memoryId), "DELETE", undefined, { expects204: true });
}
